import { useEffect, lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import "react-responsive-modal/styles.css";
// import "bootstrap/dist/css/bootstrap.min.css";
import { routes } from "./routes";
import "grapesjs/dist/css/grapes.min.css";
import "./assets/datepicker-scss/datepicker_style.css";

import { useDispatch, useSelector } from "react-redux";
import { LoginUser } from "./store/actions/authActions";
import { BackycheckFormType } from "./store/constants/constants";

/* ------------------------------------------------------------------
 * Eager imports — these must NOT be lazy.
 * Alerts renders outside <Routes>, and the guards below wrap every
 * protected route, so splitting them would add a round trip to every
 * navigation for no size benefit.
 * ------------------------------------------------------------------ */
import Alerts from "./components/common/Alert";
import ProtectedRoute from "./ProtectedRoute";
import NotRecruiterCheckRole from "./NotRecruiterCheckRole";
import RecruiterCheckRole from "./RecruiterCheckRole";
import CheckWorkSpaceType from "./CheckWorkSpaceType";
import OneSignal from "./notifications/OneSignal";

/* ------------------------------------------------------------------
 * Lazy route components.
 *
 * A plain `import X from "..."` welds the module into the main bundle.
 * `lazy(() => import("..."))` tells webpack to emit it as its own file,
 * fetched only when a route that needs it is visited.
 *
 * `lazyNamed` handles barrel modules that use named exports — React.lazy
 * requires a module whose `default` is the component, so we remap it.
 * Each barrel is imported through a single arrow function, so webpack
 * emits one shared chunk per barrel rather than one per component.
 * ------------------------------------------------------------------ */
const lazyNamed = (loader, name) =>
  lazy(() => loader().then((m) => ({ default: m[name] })));

// --- barrels ---
const publicScreens = () => import("./pages/public-screens");
const recruiterScreens = () => import("./pages/recruiter-screens");
const bothScreens = () => import("./pages/both_screens");
const candidatesBarrel = () => import("./components/candidates");

// --- public / landing ---
const Home_Page = lazy(() => import("./components/home-page/index"));
const Contact_Us = lazy(() =>
  import("./components/home-page/contactus/Contact_Us")
);
const Adminlogin = lazy(() =>
  import("./components/home-page/adminlogin/adminlogin")
);
const Index = lazy(() =>
  import("../src/components/home-page/contactus/Modals/Index")
);
const Blog = lazy(() => import("./components/home-page/blogs/Blog"));
const BlogPost = lazy(() =>
  import("./components/home-page/blogs/blogpost/BlogPost")
);
const PricingTable = lazy(() =>
  import("./components/home-page/pricingtable/PricingTable")
);
const GuestUser = lazy(() =>
  import("./components/guest-user/guest/GuestUser")
);
const TermsCondition = lazy(() =>
  import("./components/home-page/terms-condition")
);
const PrivacyPolicy = lazy(() =>
  import("./components/home-page/privacy-policy")
);
const Checkout = lazy(() =>
  import("./components/candidates/checkout_payment/checkout")
);
const FormView = lazy(() => import("./pages/public-screens/FormView"));

// --- auth screens (barrel: public-screens) ---
const Login = lazyNamed(publicScreens, "Login");
const TwoFactorPage = lazyNamed(publicScreens, "TwoFactorPage");
const Signup = lazyNamed(publicScreens, "Signup");
const ForgetPass = lazyNamed(publicScreens, "ForgetPass");
const NotFound = lazyNamed(publicScreens, "NotFound");
const VerifyPassword = lazyNamed(publicScreens, "VerifyPassword");
const Verify = lazyNamed(publicScreens, "Verify");
const NewUserLogin = lazyNamed(publicScreens, "NewUserLogin");
const RejectPage = lazyNamed(publicScreens, "RejectPage");
const ResendPage = lazyNamed(publicScreens, "ResendPage");
const GuestReference = lazyNamed(
  () => import("./pages/public-screens/reference"),
  "GuestReference"
);

// --- dashboard shell ---
const Home = lazy(() => import("./pages/home"));

// --- shared (barrel: both_screens) ---
const Jobs = lazyNamed(bothScreens, "Jobs");
const Profile = lazyNamed(bothScreens, "Profile");

// --- candidate screens ---
const DashboardActivityCandidate = lazy(() =>
  import("./pages/candidate-screens/Dashboard")
);
const InterviewScreen = lazy(() =>
  import("./pages/candidate-screens/InterviewScreen")
);
const Requests = lazy(() =>
  import("../src/components/candidate_screens/requests")
);
const SubmitsList = lazy(() =>
  import("../src/components/candidate_screens/submit")
);
const LicenseCandidate = lazy(() =>
  import("./components/candidate_screens/license")
);
const CandidateDocumentPage = lazy(() =>
  import(
    "./components/documents/document_candidate_tabel/EDocumentCandidateTabel"
  )
);
const CandidateFaqs = lazy(() => import("./components/helpdesk/cadidate-faq"));

// --- recruiter screens (barrel: recruiter-screens) ---
const WorkSpace = lazyNamed(recruiterScreens, "WorkSpace");
const Candidates = lazyNamed(recruiterScreens, "Candidates");
const Industry = lazyNamed(recruiterScreens, "Industry");
const Dashboard = lazyNamed(recruiterScreens, "Dashboard");
const Forms = lazyNamed(recruiterScreens, "Forms");
const HelpDesk = lazyNamed(recruiterScreens, "HelpDesk");
const Company = lazyNamed(recruiterScreens, "Company");
const Pricing = lazyNamed(recruiterScreens, "Pricing");
const Order = lazyNamed(recruiterScreens, "Order");
const OrderDetail = lazyNamed(recruiterScreens, "OrderDetail");
const Interview = lazy(() => import("./pages/recruiter-screens/Interview"));
const Documents = lazy(() => import("./pages/recruiter-screens/documents"));
const LicenseOrder = lazy(() =>
  import("./pages/recruiter-screens/LicenseOrder")
);
const LicenseOrderDetail = lazy(() =>
  import("./pages/recruiter-screens/LicenseOrderDetail")
);

// --- candidate management (barrel: components/candidates) ---
const AllCandidates = lazyNamed(candidatesBarrel, "AllCandidates");
const NewRequest = lazyNamed(candidatesBarrel, "NewRequest");
const SingleCandidate = lazyNamed(candidatesBarrel, "SingleCandidate");
const Formbuilderpreview = lazyNamed(candidatesBarrel, "Formbuilderpreview");
const HELPDESK = lazyNamed(candidatesBarrel, "HELPDESK");

// --- forms ---
const CreateForm = lazy(() => import("./components/forms/form-builder"));
const NewFormBuilder = lazy(() =>
  import("./components/forms/formbuilder/main")
);
const ArchivedForm = lazy(() =>
  import("./components/forms/archived-form/ArchivedForm")
);
const DraftForm = lazy(() =>
  import("./components/forms/draft-form/DraftForm")
);
const SubmissionForm = lazy(() =>
  import("./components/forms/submission-form/SubmissionForm")
);

// --- documents ---
const AllDocument = lazy(() => import("./components/documents/main/index"));
const CreateDocument = lazy(() =>
  import("./components/documents/create-form/index")
);
const ArchivedFormDocument = lazy(() =>
  import("./components/documents/archived-form/ArchivedForm")
);

// --- files ---
const AllFiles = lazy(() => import("./components/files/all_files/AllFiles"));
const ArcheivedFiles = lazy(() => import("./components/files/archeived_files"));

// --- document signing ("sinner") ---
const SelectPDF = lazy(() => import("./components/document_sinner/SelectPDF"));
const SelectSinner = lazy(() =>
  import("./components/document_sinner/SelectSinner")
);
const AssignFields = lazy(() => import("./pages/sinner/AssignFields"));
const DocSign = lazy(() => import("./pages/sinner/DocSign"));
const ViewSigningDocs = lazy(() =>
  import("./components/common/sinner/ViewSigningDocs")
);
const DocPreviewCombinedSigners = lazy(() =>
  import("./components/common/sinner/DocPreviewCombinedSigners")
);
const DocPreviewCombinedSignersPdf = lazy(() =>
  import("./components/common/sinner/DocPreviewCombinedSignersPdf")
);
const AmiciDocSign = lazy(() => import("./pages/sinner/AmiciDocSign"));
const AdminDocSign = lazy(() => import("./pages/sinner/AdminDocSign"));

// --- pricing / subscription ---
const SubscriptionPage = lazy(() =>
  import("./components/pricing/subscriptionPage/index")
);
const SubscriptionUpdate = lazy(() =>
  import("./components/pricing/upgradeSubscription/index")
);
const SubscriptionDelete = lazy(() =>
  import("./components/pricing/downgradeSubscription/index")
);

// --- misc ---
const Faqs = lazy(() => import("./components/helpdesk/faqs/"));
const Contact = lazy(() => import("./components/helpdesk/contactUs"));
const Reference = lazy(() => import("./components/reference/Reference"));
const Integrations = lazy(() => import("./components/integrations/main"));
const CreateJob = lazy(() => import("./components/jobs/createJob"));

/* Removed unused imports — none were referenced by an active route:
 *   CandidateProfile  ./components/candidate_screens/profile
 *   MainSinner        ./pages/sinner/MainSinner
 *   FormBuilder       ./components/forms/form-builder   (duplicate of CreateForm)
 *   Latest_job        .../latest-jobs/Latest_job        ) commented out below —
 *   JobDetails        .../jobsdetails/JobDetails        ) re-add if resurrected
 *
 * CreateJob was on this list too, but wrongly: routes.JOBS_CREATE still renders it
 * from three active <Route> entries, so dropping the import left it undefined and
 * that page threw ReferenceError at runtime. Restored above as a lazy import.
 * A clean build does not catch this - the failure is runtime-only.
 */

const RouteFallback = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
      width: "100%",
    }}
  >
    <CircularProgress />
  </div>
);

const App = () => {
  const auth = useSelector((state) => state.auth);
  const profiles = useSelector((state) => state?.rprofile);
  const userId = profiles?.profile?.results?.id;
  useEffect(() => {
    OneSignal(userId).catch((err) => {
      console.error("OneSignal init failed:", err);
    });
  }, [userId]);
  // useEffect(() => {
  //   if (auth?.results?.type) {
  //     localStorage.setItem("user", auth?.results?.type);
  //   }
  // }, [auth?.results?.type]);
  return (
    <>
      <Alerts />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* RESEND_VERIFICATION */}
          <Route path="/" element={<Home_Page />} />
          <Route path={routes.CONTACTS} element={<Contact_Us />} />
          <Route path={`${routes.ADMINLOGIN}`} element={<Adminlogin />} />

          <Route path={routes.Modals} element={<Index />} />
          <Route path={routes.BLOGS} element={<Blog />} />
          <Route path={routes.BLOGSPOSTS} element={<BlogPost />} />
          <Route path={routes.PRICINGTABLE} element={<PricingTable />} />
          {/* hide the jobs feature for now */}
          {/* <Route path={routes.JOBS_SEARCH} element={<Latest_job />} /> */}
          {/* <Route path={`${routes.JOBSDETAILS}/:slug`} element={<JobDetails />} /> */}
          {/* hide the jobs feature for now */}
          <Route path={routes.GUEST} element={<GuestUser />} />
          <Route
            path={routes.TERMS_AND_CONDITION}
            element={<TermsCondition />}
          />
          <Route path={routes.PRIVACY_POLICY} element={<PrivacyPolicy />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<Home />}>
              <Route
                path={`${routes.FORM_VIEW}/:formId`}
                element={<FormView />}
              />
              <Route element={<NotRecruiterCheckRole />}>
                <Route
                  path={routes.CANDIDATE_DASHBOARD}
                  element={<DashboardActivityCandidate />}
                />
                <Route path={routes.CANDIDATE_JOBS} element={<Jobs />} />
                <Route path={routes.CANDIDATE_PROFILE} element={<Profile />} />
                <Route path={routes.REQUEST_LIST} element={<Requests />} />
                <Route
                  path={routes.SUBMISSION_LIST}
                  element={<SubmitsList />}
                />
                <Route
                  path={routes.CANDIDATE_INTERVIEW}
                  element={<InterviewScreen />}
                />
                <Route
                  path={routes.CANDIDATE_LICENSE}
                  element={<LicenseCandidate isBackgroundCheck={0} />}
                />
                <Route
                  path={routes.CANDIDATE_BACKGROUND_CHECK}
                  element={<LicenseCandidate isBackgroundCheck={1} />}
                />
                <Route path={routes.HELP_DESK} element={<HELPDESK />}>
                  <Route path={routes.FAQS} element={<CandidateFaqs />} />
                  <Route path={routes.CONTACT} element={<Contact />} />
                </Route>
                <Route path={routes.REF} element={<Reference />} />
                <Route
                  path={routes.CANDIDATE_DOC_LIST}
                  element={<CandidateDocumentPage />}
                />
                <Route
                  path={`${routes.CAN_DOCUMENT.DOC_SIGN_CAN}/:id`}
                  element={<DocSign />}
                />
                <Route
                  path={`${routes.CAN_DOCUMENT.VIEW_SIGNING_DOC_CAN}/:id`}
                  element={<ViewSigningDocs />}
                />
                <Route
                  path={`${routes.CAN_DOCUMENT.DOC_PREVIEW_SINNER_CAN}/:id`}
                  element={<DocPreviewCombinedSigners />}
                />

                {/* <Route path={routes.INTEGRATIONS} element={<Reference />} /> */}
                {/* <Route path={routes.HELP_DESK} element={<HELPDESK />}>
                <Route path={routes.FAQS} element={<CandidateFaqs />} />
                <Route path={routes.CONTACT} element={<Contact />} />
              </Route> */}
              </Route>
              <Route element={<RecruiterCheckRole />}>
                <Route path={routes.PROFILE} element={<Profile />} />
                <Route path={routes.DASHBOARD} element={<Dashboard />} />
                <Route path={routes.INTERVIEW} element={<Interview />} />
                <Route element={<CheckWorkSpaceType />}>
                  <Route path={routes.WORKSPACE} element={<WorkSpace />} />
                </Route>
                <Route path={routes.INDUSTRY} element={<Industry />} />
                <Route path={routes.PRICING} element={<Pricing />}>
                  <Route
                    path={`${routes.ORDER_DETAIL}/:id`}
                    element={<OrderDetail />}
                  />
                  <Route path={`${routes.ORDER_LIST}`} element={<Order />} />
                  <Route
                    path={`${routes.LICENSE_ORDER_DETAIL}:id`}
                    element={<LicenseOrderDetail />}
                  />
                  <Route
                    path={`${routes.LICENSE_ORDER_LIST}`}
                    element={<LicenseOrder />}
                  />

                  <Route
                    path={`${routes.SUBSCRIPTION_GET}`}
                    element={<SubscriptionPage />}
                  />
                  <Route
                    path={`${routes.SUBSCRIPTION_UPDATE}`}
                    element={<SubscriptionUpdate />}
                  />
                  <Route
                    path={`${routes.SUBSCRIPTION_DELETE}`}
                    element={<SubscriptionDelete />}
                  />
                </Route>
                <Route path={routes.CANDIDATE} element={<Candidates />}>
                  <Route
                    path={routes.ALL_CANDIDATES}
                    element={<AllCandidates />}
                  />
                  <Route path={routes.NEW_REQUEST} element={<NewRequest />} />
                  <Route
                    path={`${routes.SINGLE_CANDIDATE}/:uuid`}
                    element={<SingleCandidate />}
                  />
                </Route>
                <Route
                  path={`${routes.PREVIEW_CANDIDATE_FORM}/:id/:candidateid`}
                  element={<Formbuilderpreview />}
                />

                {/* hide the jobs feature for now */}
                {/* <Route path={routes.JOBS} element={<Jobs />}></Route>
              <Route path={routes.JOBS_CREATE} element={<CreateJob />} />
              <Route
                path={`${routes.JOBS_CREATE}/:id`}
                element={<CreateJob />}
              /> */}
                {/* hide the jobs feature for now */}
                <Route path={routes.COMPANY} element={<Company />} />
                <Route path={routes.HELP_DESK_REQ} element={<HelpDesk />}>
                  <Route path={routes.FAQS_REQ} element={<Faqs />} />
                  <Route path={routes.CONTACT_REQ} element={<Contact />} />
                </Route>
                <Route path={routes.INTEGRATIONS} element={<Integrations />} />
                <Route path={routes.FORMS} element={<Forms />} />
                <Route path={routes.CREATE_NEW_FORM} element={<CreateForm />} />
                <Route
                  path={routes.FORM_BUILDER}
                  element={<NewFormBuilder />}
                />
                <Route
                  path={routes.FORM_BUILDER_EDIT}
                  element={<NewFormBuilder />}
                />
                <Route path={routes.ARCHIVED} element={<ArchivedForm />} />
                <Route path={routes.DRAFT} element={<DraftForm />} />
                <Route path={routes.SUB_LIST} element={<SubmissionForm />} />
                <Route path={routes.DOCUMENTS} element={<Documents />}>
                  <Route
                    path={routes.CREATE_NEW_DOCUMENT}
                    element={<CreateDocument />}
                  ></Route>
                  <Route
                    path={routes.ALL_DOCUMENTS}
                    element={<AllDocument />}
                  ></Route>
                  <Route
                    path={routes.ALL_ARCHIVED}
                    element={<ArchivedFormDocument />}
                  ></Route>
                </Route>

                <Route path={routes.ALL_FILES} element={<AllFiles />} />

                <Route
                  path={routes.ARCHIVED_FILES}
                  element={<ArcheivedFiles />}
                />
                <Route
                  path={routes.DOCUMENT_SIGN.UPLOAD_PDF}
                  element={<SelectPDF />}
                />

                <Route
                  path={routes.DOCUMENT_SIGN.SINNER}
                  element={<SelectSinner />}
                />
                <Route
                  path={routes.DOCUMENT_SIGN.DRAG_FIELDS}
                  element={<AssignFields />}
                />
                <Route
                  path={`${routes.DOCUMENT_SIGN.DOC_SIGN}/:id`}
                  element={<DocSign />}
                />
                <Route
                  path={`${routes.DOCUMENT_SIGN.VIEW_SIGNING_DOC}/:id`}
                  element={<ViewSigningDocs />}
                />
                <Route
                  path={`${routes.DOCUMENT_SIGN.DOC_PREVIEW_SINNER}/:id`}
                  element={<DocPreviewCombinedSigners />}
                />
              </Route>
            </Route>
          </Route>

          {/* <Route
          path={routes.HOME}
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        >
          {auth?.user !== "recruitment" ? (
            <>

              <Route
                path={routes.CANDIDATE_DASHBOARD}
                element={<DashboardActivityCandidate />}
              />
              <Route path={routes.CANDIDATE_JOBS} element={<Jobs />} />
              <Route path={routes.CANDIDATE_PROFILE} element={<Profile />} />
              <Route path={routes.REQUEST_LIST} element={<Requests />} />
              <Route path={routes.SUBMISSION_LIST} element={<SubmitsList />} />
              <Route
                path={routes.CANDIDATE_INTERVIEW}
                element={<InterviewScreen />}
              />
              <Route
                path={routes.CANDIDATE_LICENSE}
                element={<LicenseCandidate />}
              />
              <Route path={routes.HELP_DESK} element={<HELPDESK />}>
                <Route path={routes.FAQS} element={<Faqs />} />
                <Route path={routes.CONTACT} element={<Contact />} />
              </Route>
              <Route path={routes.REF} element={<Reference />} />
              <Route path={routes.INTEGRATIONS} element={<Reference />} />
              <Route
                path={`${routes.FORM_VIEW}/:formId`}
                element={<FormView />}
              />
            </>
          ) : (
            <>

              <Route path={routes.PROFILE} element={<Profile />} />
              <Route path={routes.DASHBOARD} element={<Dashboard />} />
              <Route path={routes.INDUSTRY} element={<Industry />} />
              <Route path={routes.INTERVIEW} element={<Interview />} />
              {auth?.results?.workspace_type === "recruitment" && (
                <Route path={routes.PRICING} element={<Pricing />}>
                  <Route
                    path={`${routes.ORDER_DETAIL}/:id`}
                    element={<OrderDetail />}
                  />
                  <Route path={`${routes.ORDER_LIST}`} element={<Order />} />
                  {auth?.results?.subscription_status === 1 && (
                    <>
                      <Route
                        path={`${routes.SUBSCRIPTION_GET}`}
                        element={<SubscriptionPage />}
                      />
                      <Route
                        path={`${routes.SUBSCRIPTION_UPDATE}`}
                        element={<SubscriptionUpdate />}
                      />
                      <Route
                        path={`${routes.SUBSCRIPTION_DELETE}`}
                        element={<SubscriptionDelete />}
                      />
                    </>
                  )}
                </Route>
              )}

              <Route path={routes.CANDIDATE} element={<Candidates />}>
                <Route
                  path={routes.ALL_CANDIDATES}
                  element={<AllCandidates />}
                />
                <Route path={routes.NEW_REQUEST} element={<NewRequest />} />
                <Route
                  path={`${routes.SINGLE_CANDIDATE}/:uuid`}
                  element={<SingleCandidate />}
                />
              </Route>
              <Route
                path={`${routes.PREVIEW_CANDIDATE_FORM}/:id/:candidateid`}
                element={<Formbuilderpreview />}
              />

              {auth?.results?.workspace_type === "recruitment" && (
                <Route path={routes.WORKSPACE} element={<WorkSpace />} />
              )}

              <Route path={routes.JOBS} element={<Jobs />}></Route>
              <Route path={routes.JOBS_CREATE} element={<CreateJob />} />
              <Route path={routes.COMPANY} element={<Company />} />
              <Route path={routes.HELP_DESK} element={<HelpDesk />}>
                <Route path={routes.FAQS} element={<Faqs />} />
                <Route path={routes.CONTACT} element={<Contact />} />
              </Route>
              <Route path={routes.INTEGRATIONS} element={<Integrations />} />
              <Route path={routes.FORMS} element={<Forms />} />
              <Route path={routes.CREATE_NEW_FORM} element={<CreateForm />} />
              <Route path={routes.FORM_BUILDER} element={<NewFormBuilder />} />
              <Route
                path={routes.FORM_BUILDER_EDIT}
                element={<NewFormBuilder />}
              />
              <Route path={routes.ARCHIVED} element={<ArchivedForm />} />
              <Route path={routes.DRAFT} element={<DraftForm />} />
              <Route path={routes.SUB_LIST} element={<SubmissionForm />} />
              <Route path={routes.DOCUMENTS} element={<Documents />}>
                <Route
                  path={routes.CREATE_NEW_DOCUMENT}
                  element={<CreateDocument />}
                ></Route>
                <Route
                  path={routes.ALL_DOCUMENTS}
                  element={<AllDocument />}
                ></Route>
                <Route
                  path={routes.ALL_ARCHIVED}
                  element={<ArchivedFormDocument />}
                ></Route>
              </Route>

              <Route path={routes.ALL_FILES} element={<AllFiles />} />

              <Route
                path={routes.ARCHIVED_FILES}
                element={<ArcheivedFiles />}
              />

              <Route
                path={`${routes.FORM_VIEW}/:formId`}
                element={<FormView />}
              />
            </>
          )}
        </Route> */}

          {/* public routes */}
          <Route path={routes.LOGIN} element={<Login />} />
          <Route path={routes.VERIFY_2FA} element={<TwoFactorPage />} />
          <Route path={routes.SIGNUP} element={<Signup />} />
          <Route path={routes.RESEND_VERIFICATION} element={<ResendPage />} />
          <Route path={routes.FORGET_PASSWORD} element={<ForgetPass />} />
          <Route path={`${routes.verify_token}/:token`} element={<Verify />} />
          <Route
            path={`${routes.verify_email_forget_password}/:token`}
            element={<VerifyPassword />}
          />
          <Route
            path={`${routes.NEW_USER_REQUEST}/:userNewId`}
            element={<NewUserLogin />}
          />
          <Route
            path={`${routes.NEW_USER_REJECT}/:userNewId`}
            element={<RejectPage />}
          />
          <Route
            path={`${routes.GUEST_FROM_REQUEST}/:formToken`}
            element={<FormView />}
          />
          <Route
            path={`${routes.GUEST_REFERENCE}/:Token`}
            element={<GuestReference />}
          />
          <Route
            path={`${routes.CAN_DOCUMENT.DOC_SIGN_CAN}/:id/:email`}
            element={<DocSign />}
          />
          <Route
            path={`${routes.CAN_DOCUMENT.DOC_PREVIEW_SINNER_CAN}/:id/:email`}
            element={<DocPreviewCombinedSigners />}
          />
          <Route
            path={`${routes.DOCUMENT_SIGN.DOC_PDF_VIEW}/:id`}
            element={<DocPreviewCombinedSignersPdf />}
          />
          <Route
            path={routes.BACKYCHECK_URL.AMICI_URL}
            element={<AmiciDocSign formType={BackycheckFormType.AMICI} />}
          />
          <Route
            path={routes.BACKYCHECK_URL.NPCS_URL}
            element={<AmiciDocSign formType={BackycheckFormType.NPCS} />}
          />

          <Route
            path={`${routes.BACKYCHECK_URL.NPCS_URL}/:is_organisation/:org_id/:org_client_id`}
            element={
              <AmiciDocSign
                formType={BackycheckFormType.NPCS}
                is_organisation={true}
              />
            }
          />

          {/* Routes for the Admin Form NPCS Witness Sign */}
          <Route
            path={`${routes.BACKYCHECK_URL.NPCS_URL}/:id/:is_admin_user`}
            element={
              <AdminDocSign
                formType={BackycheckFormType.NPCS}
                is_admin={true}
              />
            }
          />

          <Route
            path={`${routes.PAYMENT}/:id/:user_id/:user_type`}
            element={<Checkout />}
          />
          <Route path={routes.NOT_FOUND} element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default App;
